import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const InviteAcceptedPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [listName, setListName] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [invitationType, setInvitationType] = useState<'list' | 'project' | null>(null);

  useEffect(() => {
    const handleInvitation = async () => {
      try {
        // Get the current session to check for invitation data
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          setError('No user session found');
          setLoading(false);
          return;
        }

        // Check if there's invitation data in the user metadata
        const invitationData = session.user.user_metadata;
        const { api } = await import('../utils/api');
        
        if (invitationData?.list_id && invitationData?.list_name) {
          setListName(invitationData.list_name);
          setInvitationType('list');
          
          // Call the API to add the user to the list
          const response = await api.post('/accept-invitation', {
            list_id: invitationData.list_id,
          });

          if (!response.ok) {
            throw new Error('Failed to join the list');
          }

          // Clear the invitation data from user metadata
          await supabase.auth.updateUser({
            data: {
              list_id: null,
              list_name: null,
              invited_by: null,
            }
          });
        } else if (invitationData?.project_id && invitationData?.project_name) {
          setProjectName(invitationData.project_name);
          setInvitationType('project');
          
          // Call the API to add the user to the project
          const response = await api.post('/accept-project-invitation', {
            project_id: invitationData.project_id,
          });

          if (!response.ok) {
            throw new Error('Failed to join the project');
          }

          // Clear the invitation data from user metadata
          await supabase.auth.updateUser({
            data: {
              project_id: null,
              project_name: null,
              invited_by: null,
            }
          });
        } else {
          setError('No invitation data found');
        }
      } catch (error) {
        console.error('Error handling invitation:', error);
        setError('Failed to process invitation');
      } finally {
        setLoading(false);
      }
    };

    handleInvitation();
  }, []);

  const handleGoToLists = () => {
    if (invitationType === 'project') {
      navigate('/tasks');
    } else {
      navigate('/shopping');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Processing your invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Invitation Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleGoToLists}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Go to My Lists
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Welcome!</h1>
        <p className="text-gray-600 mb-6">
          {invitationType === 'project' 
            ? `You've successfully joined the project "${projectName}". You can now view and manage tasks in this project.`
            : `You've successfully joined the shopping list "${listName}". You can now view and edit items in this list.`
          }
        </p>
        <button
          onClick={handleGoToLists}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {invitationType === 'project' ? 'Go to Projects' : 'Go to My Lists'}
        </button>
      </div>
    </div>
  );
};

export default InviteAcceptedPage;
